<template>

    <EChart
        :options="chartOptions"
        card
        :header="$t('productsRanking')"
        overlay
        :loading="request.getLoading()"
        :error="request.getError()"
    >

        <template #nav>
            <div class="d-flex gap-3 flex-column">
                <div class="d-flex gap-3">
                    <b-dropdown :text="`${$t('type')}: ` + $t(`${currentChart}`)" toggle-class="caret" :disabled="request.getLoading()">
                        <template v-for="chartType in ['impressions', 'quantity', 'clicks', 'amount']">
                            <b-dropdown-item :key="chartType" :active="chartType === currentChart" @click="() => currentChart = chartType">{{ $t(`${chartType}`) }}</b-dropdown-item>
                        </template>
                    </b-dropdown>
                    <b-dropdown :text="$t(`${$t('show')}: ${currentNumberShown}`)" toggle-class="caret" :disabled="request.getLoading()">
                        <template v-for="numberShown in [10, 20, 50]">
                            <b-dropdown-item :key="numberShown" :active="numberShown === currentNumberShown" @click="() => currentNumberShown = numberShown">{{ numberShown }}</b-dropdown-item>
                        </template>
                    </b-dropdown>
                    <b-dropdown :text="$t(`${$t('sortBy')}: ${currentSortBy === 'summary' ? $t('total') : $t('changePercent') }`)" toggle-class="caret" :disabled="request.getLoading()">
                        <template v-for="sortBy in ['summary', 'percentDiff']">
                            <b-dropdown-item :key="sortBy" :active="sortBy === currentSortBy" @click="() => currentSortBy = sortBy">
                                <template v-if="sortBy === 'summary'">{{ $t('total') }}</template>
                                <template v-if="sortBy === 'percentDiff'">{{ $t('changePercent') }}</template>
                            </b-dropdown-item>
                        </template>
                    </b-dropdown>
                    <MultiselectAvailableCampaigns :disabled="request.getLoading()" @changeTrackBy="campaigns = $event" @change="loadData()" />
                    <Multiselect
                        v-show="showProductGroupsResults"
                        :fetch-options-function="fetchSpaceGroups"
                        :placeholder="$t('productGroup')"
                        :selected-options-label="$t('selectedGroups')"
                        searchable
                        track-by="id"
                        label="name"
                        select-label=""
                        :disabled="request.getLoading()"
                        show-checkboxes
                        show-selected
                        multiple
                        preselect-all
                        :close-on-select="false"
                        :clear-on-select="false"
                        class="width-200"
                        @inputTrackBy="groups = $event"
                        @change="loadData()"
                    />
                    <FormCategoryTreeSelect
                        v-show="!showProductGroupsResults"
                        ref="FormCategoryTreeSelect"
                        v-model="categories"
                        :disabled="request.getLoading()"
                        class="width-200"
                        preselect
                        prevent-empty-selection
                        :clearable="false"
                        @change="loadData()"
                    />
                </div>
                <div class="ml-auto d-flex gap-3 justify-content-end align-items-center">
                    <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="request.getLoading()">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    <b-form-input v-model="filterSearch" :placeholder="$t('search')" :disabled="request.getLoading()" class="width-200" />
                </div>
            </div>
        </template>

    </EChart>

</template>



<script>
    import { calcPercentDiff } from '@/utils/number';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import EChart from '@/components/common/EChart.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import Multiselect from '@/components/common/Multiselect.vue';

    export default {
        name: 'ProductRanking',
        components: {
            FormCategoryTreeSelect,
            EChart,
            MultiselectAvailableCampaigns,
            Multiselect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestImpressions: this.$req('space/ImpressionsRanking'),
                requestClicks: this.$req('space/ClicksRanking'),
                requestQuantity: this.$req('space/QuantityRanking'),
                requestAmount: this.$req('space/AmountRanking'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestSpaceGroups: this.$req('producerProductGroups/SpaceGroups'),
                currentChart: 'impressions',
                currentNumberShown: 10,
                currentSortBy: 'summary',
                campaigns: [],
                categories: [],
                groups: [],
                filterSearch: '',
                showProductGroupsResults: false,
            };
        },
        computed: {
            request() {
                if (this.currentChart === 'impressions') return this.requestImpressions;
                if (this.currentChart === 'clicks') return this.requestClicks;
                if (this.currentChart === 'quantity') return this.requestQuantity;
                if (this.currentChart === 'amount') return this.requestAmount;
                return null;
            },
            data() {
                return this.request.getData() || [];
            },
            chartData() {
                return [...this.data]
                    .map(item => ({ ...item, percentDiff: calcPercentDiff(item.summary, item.summaryComparison) }))
                    .filter(item => item.name.toLowerCase().includes(this.filterSearch.toLowerCase()))
                    .sort((a, b) => a[this.currentSortBy] < b[this.currentSortBy])
                    .slice(0, this.currentNumberShown);
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange;
                const requestParams = { startDate, endDate, compareStartDate, compareEndDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            chartSeries() {
                return [{ name: this.$t(`${this.currentChart}`), data: this.chartData.map(item => item.summary) }];
            },
            chartOptions() {
                // Przygotuj dwie listy: po lewej nazwy kategorii, po prawej wartości percentDiff
                const names = this.chartData.map(item => item.name);
                const diffs = this.chartData.map(item => item.percentDiff);

                return {
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // 1) oś po lewej – nazwy
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // 2) oś po prawej – procentowe zmiany
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                // definiujemy style rich do kolorowania
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const diff = diffs[idx];
                                    const pct = this.$format.percent(diff);
                                    if (diff > 0) return `{pos|▲${pct}}`;
                                    if (diff < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykiety wewnątrz tylko z wartościami liczbowymi
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => params.data.value,
                            },
                            data: this.chartData.map(item => ({ value: item.summary })),
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId: {
                handler: 'loadData',
            },
            timeRange: {
                handler: 'loadData',
            },
            currentChart: {
                handler: 'loadData',
            },
            showProductGroupsResults: {
                handler: 'loadData',
            },
        },
        methods: {
            loadData() {
                this.fetchRanking();
            },
            fetchRanking() {
                this.request.get(this.apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
        },
        created() {
            this.loadData();
        },
    };
</script>
